import React from 'react';
import styled from 'styled-components';
import { Prompt, Redirect } from 'react-router';
import { LayoutContext, AuthContext } from 'contexts';
import { useBreakpoint, useListing } from 'hooks';
import { JumpTo, Page, Spin } from 'components';
import UploadDocumentsForm from '../forms/UploadDocumentsForm';
import DetailsForm from '../forms/DetailsForm';
import VisibilityForm from '../forms/VisibilityForm';
import CreateListingSteps from './CreateListingSteps';
import { useNavItems, useCards } from '../hooks';
import { PageView } from '../types';

const CreateListingPage: React.FC = () => {
  const { auth, hasFeature } = React.useContext(AuthContext);
  const { scrollContainer } = React.useContext(LayoutContext);
  const { lg, md } = useBreakpoint();

  const {
    listing,
    setListing,
    initialListing,
    isListingModified,
    photos,
    setPhotos,
    setDeletedPhotos,
    thumbnail,
    setThumbnail,
    attachments,
    updateFile,
    loading,
    uploading,
    saveListing,
    distributeListing,
    submitListingForReview,
    deleteListing,
    errorMessage,
    formFieldsMissing,
    openHousesErrors,
    isFormValid,
    saved,
    documents,
    setDocuments,
    hasNarSettlement,
  } = useListing();

  const [agreedToTerms, setAgreedToTerms] = React.useState(false);

  const [currentView, setCurrentView] = React.useState(
    hasFeature('CREATE_LISTING') ? PageView.Documents : PageView.Details,
  );

  const onSaveNewListing = async ({
    moveToNextStep,
    forceDraft,
  }: {
    moveToNextStep?: boolean;
    forceDraft?: boolean;
  }) => {
    const isSaved = await saveListing({ forceDraft });
    if (isSaved) {
      if (moveToNextStep) setCurrentView(PageView.Visibility);
    } else {
      scrollContainer?.scrollTo(0, 0);
    }
    return true;
  };

  const cards = useCards(listing);
  const navItems = useNavItems({
    listing,
    photos,
    attachments,
    cards: [...cards],
    isFormValid,
  });

  const documentListError = React.useMemo(() => {
    const hasListingAgreement = documents.some(
      ({ documentType }) => documentType === 'listingAgreement',
    );

    if (!auth?.isAdmin && !hasListingAgreement) {
      return 'listingAgreement';
    }

    const allDocsLoaded = documents.every(({ url }) => url);
    if (!allDocsLoaded) return 'loading';

    return null;
  }, [auth, documents]);

  if (!auth) return null;

  if (!hasFeature('CREATE_LISTING') && !auth.isAdmin) {
    return <Redirect to="/feature-missing" />;
  }

  return (
    <Page
      title={md ? 'Create a Listing' : null}
      showBackButton={md}
      type="hollow"
      fillScreenHeight
      subHeader={
        <CreateListingSteps
          photos={photos}
          listing={listing}
          listingId={listing.id}
          currentView={currentView}
          setCurrentView={setCurrentView}
          onSaveNewListing={onSaveNewListing}
          deleteListing={deleteListing}
          saved={saved}
          loading={loading}
          documents={documents}
          distributeListing={distributeListing}
          submitListingForReview={submitListingForReview}
          isFormValid={isFormValid}
          documentListError={documentListError}
          agreedToTerms={agreedToTerms}
          uploading={uploading}
        />
      }
      asidePlacement="left"
      asideWidth={230}
      aside={
        !loading &&
        lg &&
        currentView === PageView.Details && (
          <div>
            <JumpTo items={navItems} targetOffset={25} firstItemAlwaysActive />
          </div>
        )
      }
    >
      <Prompt
        when={isListingModified && !saved && !loading}
        message="Are you sure you want to leave without saving?"
      />

      {loading ? (
        <Spin center size="large" />
      ) : (
        <StyledContainer>
          {currentView === PageView.Documents && (
            <UploadDocumentsForm
              documents={documents}
              setDocuments={setDocuments}
              documentListError={documentListError}
            />
          )}
          {currentView === PageView.Details && (
            <DetailsForm
              listing={listing}
              setListing={setListing}
              photos={photos}
              setPhotos={setPhotos}
              setDeletedPhotos={setDeletedPhotos}
              thumbnail={thumbnail}
              setThumbnail={setThumbnail}
              attachments={attachments}
              updateFile={updateFile}
              errorMessage={errorMessage}
              formFieldsMissing={formFieldsMissing}
              openHousesErrors={openHousesErrors}
              uploading={uploading}
              hasNarSettlement={hasNarSettlement}
            />
          )}
          {currentView === PageView.Visibility && (
            <VisibilityForm
              listing={listing}
              initialListing={initialListing}
              setListing={setListing}
              thumbnail={thumbnail?.url}
              agreedToTerms={agreedToTerms}
              setAgreedToTerms={setAgreedToTerms}
            />
          )}
        </StyledContainer>
      )}
    </Page>
  );
};

export default React.memo(CreateListingPage);

const StyledContainer = styled.div`
  height: 100%;
`;
